import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { NavBarComponent } from '../../layout/components/nav-bar/nav-bar.component'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { SmartAppService } from '../../services/smart-app.service'
import { ProjectService } from '../../services/project.service'
import { MediaService } from '../../services/media.service'
import {
  LogService,
  ProjectCMSMenu,
  ProjectCMSWidget,
  ProjectCMSButtonActionType,
  ProjectCMSWidgetType,
  PmsStatus,
  htmlToPlainText,
} from 'htv-lib'
import { Subscription } from 'rxjs'
import { CastService } from '../../services/cast.service'
import { LocationService } from '../../services/location.service'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { LanguageService } from '../../services/language.service'
import { RouterService, RouterPage } from '../../services/router.service'
import { ClockComponent } from '../../widgets/clock/clock.component'
import { AppGridComponent } from '../../widgets/app-grid/app-grid.component'
import { AppNameComponent } from '../../widgets/app-name/app-name.component'
import { PageService } from '../../services/page.service'
import { AppLogoComponent } from '../../widgets/app-logo/app-logo.component'
import { AppDescriptionComponent } from '../../widgets/app-description/app-description.component'
import { ChannelLogoComponent } from '../../widgets/channel-logo/channel-logo.component'
import { ChannelNumberComponent } from '../../widgets/channel-number/channel-number.component'
import { ChannelNameComponent } from '../../widgets/channel-name/channel-name.component'
import { ChannelGridComponent } from '../../widgets/channel-grid/channel-grid.component'
import { ProgramTitleComponent } from '../../widgets/program-title/program-title.component'
import { ProgramDescriptionComponent } from '../../widgets/program-description/program-description.component'
import { ProgramTimeslotComponent } from '../../widgets/program-timeslot/program-timeslot.component'
import { ChannelTVComponent } from '../../widgets/channel-tv/channel-tv.component'
import { CurrentDateComponent } from '../../widgets/current-date/current-date.component'
import { ClockDigitalComponent } from '../../widgets/clock-digital/clock-digital.component'
import { BrandLogoComponent } from '../../widgets/brand-logo/brand-logo.component'
import { ChannelBannerComponent } from '../../widgets/channel-banner/channel-banner.component'
import { IPGGridComponent } from '../../widgets/ipg-grid/ipg-grid.component'
import { ChannelService } from '../../services/channel.service'
import { LocalChannel } from '../../interfaces/local-channel'
import { CurrentLanguageComponent } from '../../widgets/current-language/current-language.component'
import { ProgramNextComponent } from '../../widgets/program-next/program-next.component'
import { ChannelBannerTextComponent } from '../../widgets/channel-banner-text/channel-banner-text.component'
import { MarriottAudioComponent } from '../../widgets/marriott-audio/marriott-audio.component'
import { MarriottSubtitleComponent } from '../../widgets/marriott-subtitle/marriott-subtitle.component'
import { StateService } from '../../services/state.service'
import { WeatherComponent } from '../../widgets/weather/weather.component'
import { MarriottWakeUpAlarmComponent } from '../../widgets/marriott-wake-up-alarm/marriott-wake-up-alarm.component'
import { MarriottAlarmRingingComponent } from '../../widgets/marriott-alarm-ringing/marriott-alarm-ringing.component'
import { MarriottPmsMessagesComponent } from '../../widgets/marriott-pms-messages/marriott-pms-messages.component'
import { MarriottPmsMessagesIndicatorComponent } from '../../widgets/marriott-pms-messages-indicator/marriott-pms-messages-indicator.component'
import { MarriottViewBillComponent } from '../../widgets/marriott-view-bill/marriott-view-bill.component'
import { StateDataService } from '../../services/state-data.service'
import { WidgetActionService } from '../../services/widget-action.service'
import { cloneDeep } from 'lodash'
import { LocalAppLaunchMode } from '../../interfaces/local-app'
import { ExternalInputService } from '../../services/external-input.service'
import { VODGridComponent } from '../../widgets/vod-grid/vod-grid.component'
import { VODPlayerComponent } from '../../widgets/vod-player/vod-player.component'
import { VODTitleComponent } from '../../widgets/vod-title/vod-title.component'
import { VODSummaryComponent } from '../../widgets/vod-summary/vod-summary.component'

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [NavBarComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class HomeComponent implements OnInit, OnDestroy {
  activatedRouteSubscription?: Subscription
  applicationFocusSubscription?: Subscription

  pageHtml: SafeHtml = ''
  currentPageId?: string
  menus: ProjectCMSMenu[] = []
  widgets: ProjectCMSWidget[] = []

  @ViewChild('vcr', { read: ViewContainerRef, static: true }) vcr!: ViewContainerRef

  constructor(
    private activatedRoute: ActivatedRoute,
    private appButtonDirective: HTVButtonControllerDirective,
    private externalInputService: ExternalInputService,
    private smartAppService: SmartAppService,
    private castService: CastService,
    private channelService: ChannelService,
    private logService: LogService,
    private mediaService: MediaService,
    private pageService: PageService,
    private projectService: ProjectService,
    private languageService: LanguageService,
    private locationService: LocationService,
    private routerService: RouterService,
    private sanitizer: DomSanitizer,
    private stateDataService: StateDataService,
    private stateService: StateService,
    private widgetActionService: WidgetActionService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.registerApplicationFocusListener()
    this.activatedRouteSubscription = this.activatedRoute.params.subscribe(params => {
      const pageId = params['id']
      if (pageId) {
        this.loadPage(pageId)
      } else {
        this.loadPage(this.projectService.getProjectPageStartId())
      }
    })
  }

  ngOnDestroy(): void {
    this.pageService.clear()
    this.activatedRouteSubscription?.unsubscribe()
    this.applicationFocusSubscription?.unsubscribe()
    this.stopAllVideoSources()
  }

  registerApplicationFocusListener(): void {
    if (this.applicationFocusSubscription) {
      this.applicationFocusSubscription?.unsubscribe()
    }
    this.applicationFocusSubscription = this.stateService.applicationFocusSubject.subscribe(
      (applicationFocus: boolean) => {
        if (!applicationFocus) {
          this.stopAllVideoSources()
        }
      }
    )
  }

  loadPage(pageId: string): void {
    this.pageService.clear()
    this.stopAllVideoSources()

    const page = this.projectService.getPageById(pageId)
    if (!page) {
      this.logService.error(`[HomeComponent] Could not find page with id = "${pageId}"`)
      return
    }

    this.currentPageId = page.id
    this.widgets = page.widgets

    const translations = this.projectService.getProjectTranslations()
    let translatedHtml = this.translateHtmlContent(page.htmlContent, translations)
    translatedHtml = this.processVideosInHtml(translatedHtml)
    translatedHtml = this.replaceHtmlVariables(translatedHtml)
    translatedHtml = this.translateMediaSources(translatedHtml, translations)
    translatedHtml = this.translateVideoSources(translatedHtml, translations)

    this.pageHtml = this.sanitizer.bypassSecurityTrustHtml(translatedHtml)

    this.menus = cloneDeep(page.menus)
      .map(menu => {
        menu.label = htmlToPlainText(translations[menu.labelTranslationId])
        if (menu.predefinedImage) {
          menu.mediaSrc = `assets/images/menus/${menu.predefinedImage}.png`
        } else if (menu.actionType !== ProjectCMSButtonActionType.SET_LANGUAGE) {
          menu.mediaSrc = this.mediaService.getMediaUrl(translations[menu.mediaTranslationId])
        } else {
          menu.mediaSrc = `assets/images/flags/${menu.actionLanguageCode}.svg`
        }
        return menu
      })
      .filter(menu => {
        // Filter out cast-related menus if cast is not available
        if (
          menu.actionType === ProjectCMSButtonActionType.OPEN_CAST ||
          menu.actionType === ProjectCMSButtonActionType.OPEN_GOOGLE_CAST
        ) {
          if (!this.castService.isCastAvailable()) {
            return false
          }
        }

        // Filter out OPEN_APP menus if app is not available
        if (menu.actionType === ProjectCMSButtonActionType.OPEN_APP) {
          if (!menu.actionAppId || !this.smartAppService.isAppAvailable(menu.actionAppId)) {
            return false
          }
        }

        // Filter out OPEN_HDMI if no hdmi port configured
        if (menu.actionType === ProjectCMSButtonActionType.OPEN_HDMI) {
          if (!menu.actionHdmiPort) {
            return false
          }
        }

        // Filter by PMS status
        if (!menu.pmsStatus) {
          return true
        }

        const isCheckedIn = this.stateDataService.guestMode
        const isCheckedOut = !this.stateDataService.guestMode

        switch (menu.pmsStatus) {
          case PmsStatus.CHKI:
            return isCheckedIn
          case PmsStatus.CHKO:
            return isCheckedOut
          case PmsStatus.CHKI_AND_CHKO:
            return true
          default:
            return true
        }
      })
      .sort((a, b) => a.position - b.position)

    this.appButtonDirective.forceRefreshSelectedButton()

    this.renderWidgets()
  }

  onNavButtonClick(navButton: ProjectCMSMenu): void {
    switch (navButton.actionType) {
      case ProjectCMSButtonActionType.START_TV: {
        let channel: LocalChannel | undefined = undefined
        if (navButton.actionTvChannelId) {
          channel = this.channelService.getChannelByGlobalChannelId(navButton.actionTvChannelId)
        }
        if (!channel) {
          channel = this.channelService.getPendingChannel()
        }
        if (channel) {
          this.channelService.openChannelPage(channel)
        }
        break
      }
      case ProjectCMSButtonActionType.PAGE_NAVIGATION:
        if (navButton.actionNavigationPageId) {
          this.routerService.goToProjectPage(navButton.actionNavigationPageId)
        }
        break
      case ProjectCMSButtonActionType.CHANGE_LANGUAGE:
        this.routerService.goToPage(RouterPage.LANGUAGE_SELECTOR)
        break
      case ProjectCMSButtonActionType.OPEN_CAST:
      case ProjectCMSButtonActionType.OPEN_GOOGLE_CAST:
        this.castService.openCastPage()
        break
      case ProjectCMSButtonActionType.OPEN_URL:
        this.routerService.goToPage(RouterPage.EXTERNAL_LINK, {
          queryParams: { actionNavigationUrl: navButton.actionNavigationUrl },
        })
        break
      case ProjectCMSButtonActionType.OPEN_APP:
        if (navButton.actionAppId) {
          this.smartAppService.launchApp(navButton.actionAppId, LocalAppLaunchMode.MENU)
        }
        break
      case ProjectCMSButtonActionType.OPEN_APPS:
        this.routerService.goToPage(RouterPage.APP_GRID)
        break
      case ProjectCMSButtonActionType.OPEN_WAKE_UP_ALARM:
        this.routerService.goToPage(RouterPage.WAKE_UP_ALARM)
        break
      case ProjectCMSButtonActionType.OPEN_TV_GUIDE:
        this.routerService.goToPage(RouterPage.CHANNEL_GRID)
        break
      case ProjectCMSButtonActionType.OPEN_IPG:
        this.routerService.goToPage(RouterPage.CHANNEL_IPG)
        break
      case ProjectCMSButtonActionType.SET_LANGUAGE:
        if (navButton.actionLanguageCode) {
          this.languageService.setSelectedLanguageFromCode(navButton.actionLanguageCode)
          this.routerService.reload()
        }
        break
      case ProjectCMSButtonActionType.SPECIFIC_ACTION:
        if (navButton.actionSpecificSubType) {
          this.widgetActionService.dispatchAction(navButton.actionSpecificSubType)
        }
        break
      case ProjectCMSButtonActionType.OPEN_HDMI:
        if (navButton.actionHdmiPort) {
          this.externalInputService.showHDMI(navButton.actionHdmiPort)
        }
        break
      case ProjectCMSButtonActionType.OPEN_VOD:
        this.routerService.goToPage(RouterPage.VOD_CATALOG)
        break
    }
  }

  translateHtmlContent(htmlContent: string, translations: Record<string, string>): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    const textNodes = doc.querySelectorAll('[data-text-id]')

    if (textNodes.length === 0) {
      return htmlContent.replace(/i18n-[^\s<]+/g, key => translations[key] || key)
    }

    textNodes.forEach(node => {
      const translation = translations[`i18n-${node.getAttribute('data-text-id')}`]
      if (translation === undefined) {
        return
      }
      const scopedStyle = node.firstElementChild?.tagName === 'STYLE' ? node.firstElementChild : null
      node.innerHTML = translation
      if (scopedStyle) {
        node.insertBefore(scopedStyle, node.firstChild)
      }
    })

    return doc.body.innerHTML
  }

  translateMediaSources(htmlContent: string, translations: Record<string, string>): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    doc.querySelectorAll('div[data-i18n-src]').forEach(div => {
      const translationKey = div.getAttribute('data-i18n-src') || ''
      const media = this.mediaService.getMedia(translations[translationKey])

      if (media && media.media.url) {
        const style = div.getAttribute('style') || ''
        const translatedUrl = this.mediaService.getMediaLocalUrl(media) ?? 'assets/images/logo.svg'

        let newStyle = undefined
        if (style.includes(`background-image: url`)) {
          newStyle = style.replace(/background-image: url\([^)]+\)/, `background-image: url(${translatedUrl})`)
        } else if (style.includes(`background:url`)) {
          newStyle = style.replace(/background:url\([^)]+\)/, `background:url(${translatedUrl})`)
        } else if (style.includes(`background: url`)) {
          newStyle = style.replace(/background: url\([^)]+\)/, `background: url(${translatedUrl})`)
        }

        if (newStyle) {
          div.setAttribute('style', newStyle)
        }
      }
    })
    return doc.body.innerHTML
  }

  translateVideoSources(htmlContent: string, translations: Record<string, string>): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    doc.querySelectorAll('video').forEach(video => {
      const translationKey = video.getAttribute('data-i18n-src') || ''
      const translatedUrl = this.mediaService.getMediaUrl(translations[translationKey]) ?? video.getAttribute('src')
      if (translatedUrl) {
        video.setAttribute('src', translatedUrl)
        video.load()
        video.addEventListener('canplay', () => {
          video.play().catch(error => {
            this.logService.error(`Error during video playback : ${error}`)
          })
        })
        video.addEventListener('error', () => {
          const mediaError = video.error
          if (mediaError) {
            let errorMessage = ''
            switch (mediaError.code) {
              case mediaError.MEDIA_ERR_ABORTED:
                errorMessage = 'Media recovery cancelled by user.'
                break
              case mediaError.MEDIA_ERR_NETWORK:
                errorMessage = 'Network error - media could not be recovered.'
                break
              case mediaError.MEDIA_ERR_DECODE:
                errorMessage = 'Error decoding media.'
                break
              case mediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorMessage = 'Media not supported.'
                break
              default:
                errorMessage = 'Unknown error.'
            }
            this.logService.error(`Error loading video from ${video.src} : code ${mediaError.code} - ${errorMessage}`)
          } else {
            this.logService.error(`Error loading video from ${video.src}: no error details available.`)
          }
        })
      } else {
        video.remove()
      }
    })
    return doc.body.innerHTML
  }

  // Note: This will show '' when there is no occupant (or when variable is found but nothing is associated with it)
  replaceHtmlVariables(htmlContent: string): string {
    const variables: Record<string, string | null> = {
      '{Guest Name}': this.locationService.getLocationOccupantLastName() ?? null,
      '{title}': this.locationService.getLocationOccupantSalutation() ?? null,
      '{firstName}': this.locationService.getLocationOccupantFirstName() ?? null,
      '{lastName}': this.locationService.getLocationOccupantLastName() ?? null,
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    const divs = doc.querySelectorAll('div')

    divs.forEach(div => {
      Object.keys(variables).forEach(variableName => {
        if (this.divMatchesText(div, variableName)) {
          if (variables[variableName]) {
            div.innerHTML = div.innerHTML.replace(variableName, variables[variableName] ?? '')
          } else {
            div.innerHTML = ''
          }
        }
      })
    })

    return doc.body.innerHTML
  }

  divMatchesText(element: HTMLElement, targetText: string): boolean {
    return Array.from(element.childNodes).some(
      node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim().includes(targetText)
    )
  }

  processVideosInHtml(htmlContent: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    doc.querySelectorAll('video').forEach(video => {
      video.removeAttribute('controls')
      video.removeAttribute('volume')
      video.removeAttribute('playsInline')
      video.removeAttribute('muted')

      video.setAttribute('preload', 'auto')
      video.setAttribute('autoplay', '')
      video.setAttribute('type', 'video/mp4')
      video.setAttribute('loop', '')
      video.style.backgroundColor = '#000'

      const audioEnabled = video.getAttribute('data-audio-enabled') === 'true'
      if (!audioEnabled) {
        video.setAttribute('muted', '')
      }
    })

    return doc.body.innerHTML
  }

  stopAllVideoSources() {
    document.querySelectorAll('video').forEach(video => {
      video.pause()
      video.src = ''
      video.removeAttribute('src')
      video.remove()
    })
  }

  renderWidgets(): void {
    this.vcr.clear()

    this.widgets.forEach(widget => {
      let component: any | undefined = undefined
      switch (widget.type) {
        case ProjectCMSWidgetType.CLOCK:
          component = this.vcr.createComponent(ClockComponent)
          break
        case ProjectCMSWidgetType.CLOCK_DIGITAL:
          component = this.vcr.createComponent(ClockDigitalComponent)
          break
        case ProjectCMSWidgetType.CURRENT_DATE:
          component = this.vcr.createComponent(CurrentDateComponent)
          break
        case ProjectCMSWidgetType.BRAND_LOGO:
          component = this.vcr.createComponent(BrandLogoComponent)
          break
        case ProjectCMSWidgetType.APP_NAME:
          component = this.vcr.createComponent(AppNameComponent)
          break
        case ProjectCMSWidgetType.APP_LOGO:
          component = this.vcr.createComponent(AppLogoComponent)
          break
        case ProjectCMSWidgetType.APP_DESCRIPTION:
          component = this.vcr.createComponent(AppDescriptionComponent)
          break
        case ProjectCMSWidgetType.APP_GRID:
          component = this.vcr.createComponent(AppGridComponent)
          break
        case ProjectCMSWidgetType.CHANNEL_BANNER_CONTAINER:
          component = this.vcr.createComponent(ChannelBannerComponent)
          break
        case ProjectCMSWidgetType.CHANNEL_BANNER_TEXT:
          component = this.vcr.createComponent(ChannelBannerTextComponent)
          break
        case ProjectCMSWidgetType.CHANNEL_TV: {
          // Force ChannelTV widget as first child because we're not handling z-index
          const options = { index: 0 }
          component = this.vcr.createComponent(ChannelTVComponent, options)
          break
        }
        case ProjectCMSWidgetType.CHANNEL_LOGO:
          component = this.vcr.createComponent(ChannelLogoComponent)
          break
        case ProjectCMSWidgetType.CHANNEL_NUMBER:
          component = this.vcr.createComponent(ChannelNumberComponent)
          break
        case ProjectCMSWidgetType.CHANNEL_NAME:
          component = this.vcr.createComponent(ChannelNameComponent)
          break
        case ProjectCMSWidgetType.CHANNEL_GRID:
          component = this.vcr.createComponent(ChannelGridComponent)
          break
        case ProjectCMSWidgetType.LANGUAGE:
          component = this.vcr.createComponent(CurrentLanguageComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_AUDIO:
          component = this.vcr.createComponent(MarriottAudioComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_SUBTITLE:
          component = this.vcr.createComponent(MarriottSubtitleComponent)
          break
        case ProjectCMSWidgetType.WEATHER:
          component = this.vcr.createComponent(WeatherComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_WAKE_UP_ALARM:
          component = this.vcr.createComponent(MarriottWakeUpAlarmComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_ALARM_RINGING:
          component = this.vcr.createComponent(MarriottAlarmRingingComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_PMS_MESSAGES:
          component = this.vcr.createComponent(MarriottPmsMessagesComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_PMS_MESSAGES_INDICATOR:
          component = this.vcr.createComponent(MarriottPmsMessagesIndicatorComponent)
          break
        case ProjectCMSWidgetType.MARRIOTT_VIEW_BILL:
          component = this.vcr.createComponent(MarriottViewBillComponent)
          break
        case ProjectCMSWidgetType.PROGRAM_TITLE:
          component = this.vcr.createComponent(ProgramTitleComponent)
          break
        case ProjectCMSWidgetType.PROGRAM_DESCRIPTION:
          component = this.vcr.createComponent(ProgramDescriptionComponent)
          break
        case ProjectCMSWidgetType.PROGRAM_NEXT:
          component = this.vcr.createComponent(ProgramNextComponent)
          break
        case ProjectCMSWidgetType.PROGRAM_TIMESLOT:
          component = this.vcr.createComponent(ProgramTimeslotComponent)
          break
        case ProjectCMSWidgetType.IPG:
          component = this.vcr.createComponent(IPGGridComponent)
          break
        case ProjectCMSWidgetType.VOD_GRID:
          component = this.vcr.createComponent(VODGridComponent)
          break
        case ProjectCMSWidgetType.VOD_TITLE:
          component = this.vcr.createComponent(VODTitleComponent)
          break
        case ProjectCMSWidgetType.VOD_PLAYER:
          component = this.vcr.createComponent(VODPlayerComponent)
          break
        case ProjectCMSWidgetType.VOD_SUMMARY:
          component = this.vcr.createComponent(VODSummaryComponent)
          break
        default:
          break
      }
      if (component) {
        component.setInput('config', widget)
        component.changeDetectorRef.detectChanges()
      }
    })
  }
}
